import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Profile } from '../../types/profile';
import {
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  from,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { Auth, authState, signOut } from '../../core/firebase/auth';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ProfileService } from '../../services/profile.service';
import {
  RoleService,
  UserRole,
  ActiveContext,
} from '../../services/role.service';
import { School } from '../../types/school';
import { SchoolAuthorityDetail } from '../../types/rest-api';
import {
  BreakpointObserver,
  Breakpoints,
  MediaMatcher,
} from '@angular/cdk/layout';
import { clearAllOnboardingSessionStores } from '../../pages/onboarding/onboarding-session-storage';
import {
  Analytics,
  logEvent,
  setAnalyticsCollectionEnabled,
  setUserId,
} from '../../core/firebase/analytics';
import { StreamUserService } from '../../../modules/chat/services/stream-user.service';
import { NavigationService } from '../../../modules/chat/services/navigation.service';
import { ChatClientService } from 'stream-chat-angular';
import { environment } from '../../../environments/environment';
import { patchChatState, patchDeviceState } from '../../signals/state';
import { PushNotificationService } from '../../services/push/push-notification.service';
import { AppBadgeService } from '../../services/push/app-badge.service';
import {
  MatSidenavContainer,
  MatSidenav,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { NgClass } from '@angular/common';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import {
  AUTH_ROUTE,
  PublicToolbarComponent,
} from '../../components/public-toolbar/public-toolbar.component';
import { PublicFooterComponent } from '../../components/public-footer/public-footer.component';
import { BreadcrumbsComponent } from '../../components/breadcrumbs/breadcrumbs.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

const SCROLL_TOP_KEY = 'bnScrollTop';

function savedScrollTop(state: unknown): number | null {
  const value = (state as Record<string, unknown> | null)?.[SCROLL_TOP_KEY];
  return typeof value === 'number' ? value : null;
}

@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrl: './default-layout.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatSidenavContainer,
    NgClass,
    MatSidenav,
    NavbarComponent,
    MatSidenavContent,
    PublicToolbarComponent,
    PublicFooterComponent,
    BreadcrumbsComponent,
    RouterOutlet,
    RouterLink,
    FooterComponent,
    MatToolbar,
    MatIconButton,
    MatIcon,
  ],
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  auth = inject(Auth, { optional: true });
  router = inject(Router);
  roleService = inject(RoleService);
  profileService = inject(ProfileService);
  changeDetectorRef = inject(ChangeDetectorRef);
  media = inject(MediaMatcher);
  private analytics = inject(Analytics);
  private streamUserService = inject(StreamUserService);
  private navigationService = inject(NavigationService);
  private chatService = inject(ChatClientService);
  private pushNotifications = inject(PushNotificationService);
  private appBadge = inject(AppBadgeService);
  private responsive = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);

  isNavbarOpen = false;
  isSchoolProfileRoute = false;
  isAuthRoute = false;
  adminSchools: School[] = [];
  adminAuthorities: SchoolAuthorityDetail[] = [];
  profile?: Profile;
  profileLoading = true;
  permissionsSubscription?: Subscription;
  userRole: UserRole = 'fachkraft';
  uid?: string;
  isChat: boolean = false;
  logoutInProgress: boolean = false;

  // The router only restores `window` scroll, but pages scroll inside
  // `#scroll-area`. Like the browser does for documents, each history entry
  // keeps its own position in `history.state`, so it also survives a reload.
  private restoreScrollTop: number | null = null;
  private restoringScroll = false;
  private scrollRun = 0;
  private scrollSaveTimer?: ReturnType<typeof setTimeout>;

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  constructor() {
    const changeDetectorRef = this.changeDetectorRef;
    const media = this.media;

    this.mobileQuery = media.matchMedia('(max-width: 1023px)');
    this._mobileQueryListener = () => {
      changeDetectorRef.detectChanges();
    };
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.navigationService.startSaveHistory();

    // Created during the app's first navigation (fresh load, reload, or back
    // into the app), whose NavigationStart has already fired.
    if (!this.router.currentNavigation()?.previousNavigation) {
      this.restoreScrollTop = savedScrollTop(history.state);
    }
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => {
        clearTimeout(this.scrollSaveTimer);
        if (e.navigationTrigger === 'popstate') {
          // The browser has already switched to the entry we go back to.
          this.restoreScrollTop = savedScrollTop(e.restoredState);
        } else {
          // Still on the entry we leave: store where it was scrolled to.
          this.saveScrollTop();
          this.restoreScrollTop = null;
        }
      });

    const onScroll = (event: Event) => {
      if ((event.target as HTMLElement | null)?.id !== 'scroll-area') return;
      clearTimeout(this.scrollSaveTimer);
      this.scrollSaveTimer = setTimeout(() => this.saveScrollTop(), 150);
    };
    // Scrolling must not run change detection.
    // A reload can come before the debounced save: store the exact position,
    // which also wins over a `#fragment` left in the URL.
    const onPageHide = () => {
      clearTimeout(this.scrollSaveTimer);
      this.saveScrollTop();
    };
    inject(NgZone).runOutsideAngular(() => {
      document.addEventListener('scroll', onScroll, true);
      window.addEventListener('pagehide', onPageHide);
    });
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.scrollSaveTimer);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('pagehide', onPageHide);
    });
  }

  private saveScrollTop() {
    const scrollEl = document.getElementById('scroll-area');
    if (!scrollEl || this.restoringScroll) return;
    history.replaceState(
      { ...history.state, [SCROLL_TOP_KEY]: scrollEl.scrollTop },
      ''
    );
  }

  async ngOnInit() {
    // Nav sections (Meine Organisation vs Meine Schule) follow API role, not the
    // entity switcher: a Schulgemeinde user who picks a Schuleinheit in the
    // dropdown must still see org links (Struktur, Träger-Profil, Inserate).
    this.roleService.activeContext$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.userRole = this.roleService.role;
      });

    const initialUrl = this.router.url.split(/[?;]/)[0];
    this.isSchoolProfileRoute = /^\/schools\/[^/]+/.test(initialUrl);
    this.isAuthRoute = AUTH_ROUTE.test(initialUrl);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => {
        // Mobile `mat-sidenav` is `over` + backdrop while open. In-app navigations
        // (e.g. `/schools/…` from list or breadcrumbs) do not call `toggleNavbar()`,
        // so `isNavbarOpen` could stay true and the backdrop keeps eating clicks on
        // main content + (depending on browser) feels like a frozen page on staging.
        if (this.mobileQuery.matches) {
          this.isNavbarOpen = false;
        }
        if (this.roleService.loaded) {
          this.syncContextFromUrl();
        }
        const url = e.urlAfterRedirects.split(/[?;]/)[0];
        this.isSchoolProfileRoute = /^\/schools\/[^/]+/.test(url);
        this.isAuthRoute = AUTH_ROUTE.test(url);
      });

    // Start loading role data as soon as a user is authenticated so it runs in parallel
    // with getMe(). The `authState` gate is required: this layout is also mounted on /login
    // (sibling child route, not behind authGuard), so an unconditional `loadRole()` would
    // fire while the user is still unauthenticated. Both school/authority API calls return
    // 401, `catchError` swallows them, and the service settles with `_loaded = true` plus a
    // stale `fachkraft` role — which the post-login getMe completion then trusts and applies
    // to the navbar until a hard refresh rebuilds the singleton.
    if (!this.roleService.loaded && this.auth) {
      authState(this.auth)
        .pipe(
          filter(user => !!user),
          take(1),
          switchMap(() => this.roleService.loadRole()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          if (this.profile?.id) {
            this.syncContextFromUrl();
            this.applyRoleData();
          }
        });
    }

    // `loadRole()` from onboarding / redirects updates RoleService before profile re-emits;
    // keep nav entity lists in sync so the sidebar upgrade row appears without a full reload.
    combineLatest([
      this.roleService.mySchools$,
      this.roleService.myAuthorities$,
    ])
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.profile?.id || !this.roleService.loaded) {
          return;
        }
        this.applyRoleData();
        this.changeDetectorRef.markForCheck();
      });

    this.profileService.getMe(true).subscribe(profile => {
      this.profileService.getCurrentProfileSubscription(p => {
        this.profile = p?.id ? p : profile;
        this.profileLoading = false;
        if (this.profile?.id) {
          if (this.roleService.loaded) {
            this.syncContextFromUrl();
            this.applyRoleData();
          }
          this.appBadge.start();
          void this.pushNotifications.syncExisting(this.profile.id);
          // else: the loadRole() started above will call applyRoleData() when it completes

          this.streamUserService
            .getToken(this.profile.id)
            .pipe(
              switchMap(token =>
                from(
                  this.chatService.init(
                    environment.stream.chat.apiKey,
                    this.profile!.id,
                    token
                  )
                )
              )
            )
            .subscribe(() => patchChatState({ init: true }));
        }
      });
    });

    this.profileService.getPreferences().subscribe(preferences => {
      setAnalyticsCollectionEnabled(
        this.analytics,
        preferences.analyticTrackingEnabled
      );
    });

    this.navigationService.getCurrentUrl().subscribe(url => {
      this.isChat = url.startsWith('chat');
    });

    this.responsive
      .observe([Breakpoints.HandsetLandscape, Breakpoints.HandsetPortrait])
      .subscribe(result => patchDeviceState(result.matches));
  }

  private applyRoleData(): void {
    this.userRole = this.roleService.role;
    this.adminSchools = this.roleService.mySchools
      .filter(s => s.school.parent_id == null)
      .map(s => ({ id: s.school.id, name: s.school.name }) as School);
    this.adminAuthorities = this.roleService.myAuthorities.map(
      a => a.school_authority
    );
  }

  async logout() {
    this.logoutInProgress = true;
    try {
      clearAllOnboardingSessionStores();
      if (this.permissionsSubscription) {
        this.permissionsSubscription.unsubscribe();
      }
      logEvent(this.analytics, 'logout');
      setUserId(this.analytics, null);
      await this.pushNotifications.unregisterForLogout();
      await this.appBadge.reset();
      try {
        await firstValueFrom(this.streamUserService.logout());
      } catch (e) {
        console.warn('Error logging out from stream:', e);
      }
      if (this.auth) {
        await signOut(this.auth);
      }
      setTimeout(async () => {
        localStorage.clear();
        sessionStorage.clear();
        await this.router.navigate(['login']);
        window.location.reload();
      }, 300);
    } catch (e) {
      this.logoutInProgress = false;
    }
  }

  toggleNavbar() {
    logEvent(this.analytics, 'toggle_navbar');
    this.isNavbarOpen = !this.isNavbarOpen;
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.responsive.ngOnDestroy();
  }

  /**
   * Dashboard home (`/`) — top inset tuned so content aligns with the vertical band
   * between the sidebar wordmark and the entity switcher (2xl), and sits comfortably
   * below the compact logo rail (md).
   */
  routerOutletVerticalPaddingClass(): string {
    // `router.url` enthält ggf. Matrix- und Query-Parameter (z. B.
    // `/login;returnUrl=/?foo=bar`); für den path-Vergleich beides abschneiden.
    const path = this.router.url.split(/[?;]/)[0];
    if (path === '/' || path === '') {
      return 'py-0 md:pt-10 md:pb-16 2xl:pt-[60px] 2xl:pb-20';
    }
    // No footer on login and sign-up, so the page itself keeps space below.
    if (AUTH_ROUTE.test(path)) {
      return 'pt-0 pb-12 md:py-16 2xl:py-20';
    }
    // Stellen list: its own pb-4 plus the footer's pt-8 already space it out.
    if (path === '/jobs') {
      return 'py-0 md:pt-16 md:pb-4 2xl:pt-20 2xl:pb-6';
    }
    return 'py-0 md:py-16 2xl:py-20';
  }

  onActivate(_event: unknown) {
    const scrollEl = document.querySelector(
      '#scroll-area'
    ) as HTMLElement | null;
    if (!scrollEl) {
      return;
    }
    scrollEl.scrollTop = 0;
    scrollEl.classList.remove('invisible');
    this.restoringScroll = false;
    // Cancels retries still pending from the previous page.
    const run = ++this.scrollRun;

    const restoreTop = this.restoreScrollTop;
    this.restoreScrollTop = null;
    if (restoreTop !== null) {
      // Page content usually loads async, so retry until it is tall enough.
      // Stay hidden meanwhile, or the top of the page flashes before the jump.
      scrollEl.classList.add('invisible');
      this.restoringScroll = true;
      const tryRestore = (attempt: number) => {
        if (run !== this.scrollRun) return;
        scrollEl.scrollTop = restoreTop;
        if (Math.abs(scrollEl.scrollTop - restoreTop) > 1 && attempt < 40) {
          setTimeout(() => tryRestore(attempt + 1), 50);
          return;
        }
        scrollEl.classList.remove('invisible');
        this.restoringScroll = false;
      };
      setTimeout(() => tryRestore(0), 0);
      return;
    }

    const tree = this.router.parseUrl(this.router.url);
    const fragment = tree.fragment;
    if (!fragment) {
      return;
    }

    const tryScrollToFragment = (attempt: number) => {
      if (run !== this.scrollRun) return;
      const target = document.getElementById(fragment);
      if (target) {
        target.scrollIntoView({ block: 'start' });
        return;
      }
      if (attempt < 40) {
        setTimeout(() => tryScrollToFragment(attempt + 1), 50);
      }
    };
    setTimeout(() => tryScrollToFragment(0), 0);
  }

  /** True when the fixed hamburger toolbar is shown; must match top offset on `.sidenav-container`. */
  public showMobileToolbar(): boolean {
    const p = this.profile;
    return (
      this.mobileQuery.matches && !!p?.id && !!p.isComplete && !this.isChat
    );
  }

  private syncContextFromUrl(): void {
    const url = this.router.url.split('?')[0];
    const schoolMatch = url.match(/^\/my-schools\/([^/]+)/);
    const authorityMatch = url.match(/^\/my-organization\/([^/]+)/);
    const current = this.roleService.activeContext;

    if (schoolMatch) {
      const id = schoolMatch[1];
      if (current?.type === 'school' && current.id === id) return;
      const school = this.roleService.mySchools.find(
        s => s.school.id === id
      )?.school;
      if (school) {
        this.roleService.switchContext({
          type: 'school',
          id,
          name: school.name ?? '',
        });
      }
      return;
    }

    if (authorityMatch) {
      const id = authorityMatch[1];
      if (current?.type === 'authority' && current.id === id) return;
      // Browsing a profile is not a context switch. The hierarchy tree links to the Träger
      // (`/my-organization/:id`) and to its units/Standorte (`/my-organization/:id/schools/:id`),
      // and following any of those while working in a Schuleinheit should leave the sidebar on
      // that unit. Work destinations — `/structure`, `/jobs`, `/edit/…` — still sync, as does the
      // top switcher, which calls `switchContext` itself before navigating. A null context still
      // syncs too, so cold direct-URL entry keeps working.
      const isProfileBrowse =
        /^\/my-organization\/[^/]+(\/schools\/[^/]+)?\/?$/.test(url);
      if (isProfileBrowse && current?.type === 'school') return;
      const authority = this.roleService.myAuthorities.find(
        a => a.school_authority.id === id
      )?.school_authority;
      if (authority) {
        this.roleService.switchContext({
          type: 'authority',
          id,
          name: authority.name,
        });
      }
    }
  }

  onContextSwitch(ctx: ActiveContext): void {
    this.roleService.switchContext(ctx);

    const targetUrl =
      ctx.type === 'authority'
        ? `/my-organization/${ctx.id}/structure`
        : `/my-schools/${ctx.id}/structure`;

    if (this.router.url === targetUrl) {
      return;
    }
    this.router.navigateByUrl(targetUrl);
  }
}
